import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { AuthStateService } from '../../../../core/services/auth-state/auth-state-service.service';

type Matrix = Record<string, Record<string, number>>;

@Component({
  selector: 'app-contract-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    CommonModule,
  ],
  templateUrl: './contract-form.component.html',
  styleUrl: './contract-form.component.css',
})
export class ContractFormComponent {
  @Input() campaignData?: any;
  @Input() ladata: any = [];
  ivaEnabled = true;
  originalMatrix: number[][] = []; // Guardará los valores originales sin IVA
  contractForm: FormGroup = new FormGroup({});

  insuranceNotesOptions = [
    { key: 'all_years', label: 'Todos los años del contrato' },
    { key: 'first_year', label: 'SOLO el primer año del contrato' },
    { key: 'other', label: 'Otros' },
  ];
  type_: any[] = ['renting', 'leasing'];
  taxRate: number = 0.21;
  constructor(
    private fb: FormBuilder,
    private fb2: FormBuilder,
    private alertService: AlertService,
    private authStateService: AuthStateService,
  ) {
    const misHeaders = ['36', '48', '60'];
    const misFilas = ['10000', '15000', '20000', '25000'];

    this.initMatrix(
      4,             // nº filas
      3,             // nº columnas
      misHeaders,    // encabezados elegidos
      misFilas       // nombres de filas elegidos
    );

    this.contractForm = this.fb.group({
      type: ['', Validators.required],
      include_insurance: [false, Validators.required],
      include_maintenance: [false, Validators.required],
      include_tires: [false, Validators.required],
      include_replacement_vehicle: [false, Validators.required],
      initial_payment: ['', Validators.required],
      insurance_deductible: [null],
      insurance_notes_option: [''],
      insurance_notes: [''],
      maintenance_notes: [''],
      tires_notes: [''],
      replacement_vehicle_notes: [''],
      matrix_tax: [0, Validators.required],
      iva_included: [false, Validators.required],

    });
      this.taxRate = this.authStateService.getSupplierTax() || 0.21;
  }
  @ViewChild('ivaCheckbox') ivaCheckbox!: ElementRef;
  @Output() matrixDimensionsChanged = new EventEmitter<{ headers: string[], rowLabels: string[] }>();
  @Output() matrixValuesChanged = new EventEmitter<void>();
  @Output() ivaToggled = new EventEmitter<boolean>();

  ngOnInit(): void {
    // Sync insurance_notes_option → insurance_notes
    this.contractForm.get('insurance_notes_option')?.valueChanges.subscribe(option => {
      if (option === 'other') {
        this.contractForm.get('insurance_notes')?.setValue('');
      } else if (option) {
        const label = this.insuranceNotesOptions.find(o => o.key === option)?.label || '';
        this.contractForm.get('insurance_notes')?.setValue(label);
      }
    });
  }

  ngAfterViewInit() { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes['campaignData']) {
          if (this.campaignData) {
          this.refrescarFormulario();
        }
      }
    }
  }


  refrescarFormulario() {
    this.updateFieldAccessibility();
    for (const key in this.campaignData) {
      const control = this.contractForm.controls[key];
      const value = this.campaignData[key];
      if (control) {
        control.setValue(value);
      }
    }

    // Prefill insurance_notes_option based on existing insurance_notes value
    const existingNotes = this.contractForm.get('insurance_notes')?.value || '';
    const matchedOption = this.insuranceNotesOptions.find(o => o.label === existingNotes);
    this.contractForm.get('insurance_notes_option')?.setValue(
      matchedOption ? matchedOption.key : (existingNotes ? 'other' : ''),
      { emitEvent: false }
    );

    if (this.campaignData?.matrix_total) {
      this.loadMatrix(this.campaignData?.matrix_total);
    }
    this.emitDimensionsChanged();
    this.matrixValuesChanged.emit();
    this.alertService.close();
  }

  updateFieldAccessibility() {
    var rules = this.campaignData?.rules;
    // Deshabilitar todos los campos por defecto
    for (const field in this.contractForm.controls) {
      if (this.contractForm.controls.hasOwnProperty(field)) {
        this.contractForm.get(field)?.disable();
      }
    }

    // Habilitar solo los campos con valor 'w' en las reglas
    for (const field in rules) {
      if (rules.hasOwnProperty(field) && rules[field] === 'w') {
        this.contractForm.get(field)?.enable();
      }
    }

    // insurance_notes_option is FE-only, enable it when insurance_notes is enabled
    if (this.contractForm.get('insurance_notes')?.enabled) {
      this.contractForm.get('insurance_notes_option')?.enable();
    }
  }

  changeType(value: any) {
    var type: any;
    if (typeof value === 'string') {
      type = value;
    } else {
      type = value.target.value;
    }

    if (type == 'renting') {
      this.contractForm.get('include_tires')?.setValue(1);
      this.contractForm.get('include_insurance')?.setValue(1);
      this.contractForm.get('include_maintenance')?.setValue(1);
      this.contractForm.get('include_replacement_vehicle')?.setValue(0);
    } else {
      this.contractForm.get('include_tires')?.setValue(0);
      this.contractForm.get('include_insurance')?.setValue(1);
      this.contractForm.get('include_maintenance')?.setValue(1);
      this.contractForm.get('include_replacement_vehicle')?.setValue(0);
    }
  }

  checkUpdateForm(): boolean {
    if (this.contractForm.dirty) {
      return true;
    } else {
      return false;
    }
  }


  matrixForm: FormGroup = this.fb2.group({
    headers: this.fb2.array<FormControl>([]),                // ← nombres de columnas
    rows: this.fb2.array<FormGroup>([])                   // ← filas completas
  });

  /* ---------- Getters de comodidad ---------- */
  get headers(): FormArray<FormControl> { return this.matrixForm.get('headers') as FormArray; }
  get rows(): FormArray<FormGroup> { return this.matrixForm.get('rows') as FormArray; }

  columnCount(): number { return this.headers.length; }


  /* --------------- Construcción inicial ---------------- */
  private initMatrix(
    rowCnt: number,
    colCnt: number,
    headerNames: string[] = [],      // ← nombres personalizados
    rowNames: string[] = []       // ← idem para filas
  ): void {

    /* -- Encabezados -- */
    for (let c = 0; c < colCnt; c++) {
      const name = headerNames[c] ?? `Col ${c + 1}`;
      this.headers.push(this.fb.control(name));
    }

    /* -- Filas -- */
    for (let r = 0; r < rowCnt; r++) {
      const label = rowNames[r] ?? `Fila ${r + 1}`;
      this.rows.push(this.buildRow(label, colCnt));
    }
  }

  private buildRow(label = '', colCnt = 0): FormGroup {
    const cells = Array.from({ length: colCnt }, () => this.fb.control(''));
    return this.fb.group({
      label: this.fb.control(label),            // ← nombre de fila
      cells: this.fb.array(cells)               // ← celdas de datos
    });
  }

  /* ---------------- Acciones de UI ---------------- */
  addColumn(): void {
    this.headers.push(this.fb.control(''));
    // Añade una celda vacía a cada fila existente
    this.rows.controls.forEach(row =>
      (row.get('cells') as FormArray).push(this.fb.control(''))
    );
    this.emitDimensionsChanged();
  }

  addRow(): void {
    const newRow = this.buildRow('', this.columnCount());
    this.rows.push(newRow);
    this.emitDimensionsChanged();
  }


  loadMatrix(data: { [month: string]: { [kms: string]: number } }) {
    const months = Object.keys(data)
      .map(Number)
      .sort((a, b) => a - b)
      .map(String);

    const kmsSet = new Set<string>();
    Object.values(data).forEach(row => {
      Object.keys(row).forEach(km => kmsSet.add(km));
    });

    const kmsList = Array.from(kmsSet)
      .map(Number)
      .sort((a, b) => a - b)
      .map(String);

    this.headers.clear();
    months.forEach(month => this.headers.push(new FormControl(month)));

    this.rows.clear();
    this.originalMatrix = []; // Reiniciar original

    kmsList.forEach(km => {
      const originalRow: number[] = [];

      const rowGroup = this.fb2.group({
        label: [km],
        cells: this.fb2.array(
          months.map(month => {
            const value = data[month]?.[km] ?? null;
            originalRow.push(value);
            return new FormControl(value);
          })
        )
      });

      this.originalMatrix.push(originalRow);
      this.rows.push(rowGroup);
    });
  }


  removeIva(): void {
    this.originalMatrix = this.rows.controls.map((rowGroup: FormGroup) => {
      const cells = rowGroup.get('cells') as FormArray;
      return cells.controls.map(control => control.value);
    });

    this.rows.controls.forEach((rowGroup, rowIndex) => {
      const cells = rowGroup.get('cells') as FormArray;
      cells.controls.forEach((control, cellIndex) => {
        const originalValue = this.originalMatrix[rowIndex]?.[cellIndex];


        if (!this.ivaEnabled) {
          const withoutIva = originalValue != null ? +(originalValue / (1 + this.taxRate)).toFixed(2) : null;
          control.setValue(withoutIva);
          control.disable();
        } else {
          control.setValue(originalValue);
          control.enable();
        }
      });

      const label = rowGroup.get('label');
      !this.ivaEnabled ? label?.disable() : label?.enable();
    });

    this.headers.controls.forEach(h => {
      !this.ivaEnabled ? h.disable() : h.enable();
    });
  }

  applyIva(): void {
    this.rows.controls.forEach((rowGroup, rowIndex) => {
      const cells = rowGroup.get('cells') as FormArray;
      cells.controls.forEach((control, cellIndex) => {
        const originalValue = this.originalMatrix[rowIndex]?.[cellIndex];
        control.setValue(originalValue);
        control.enable();

      });
      const label = rowGroup.get('label');
      label?.enable();
    });
    this.headers.controls.forEach(h => {
      h.enable();
    });
  }

  /* ------------ Envío y JSON resultante ------------- */
  obtenerMatriz(): any {
    const matrix: Record<string, Record<string, number>> = {};

    this.rows.controls.forEach(rowCtrl => {
      const rowKey = rowCtrl.get('label')!.value;
      const cells = (rowCtrl.get('cells') as FormArray).value;


      this.headers.controls.forEach((hdrCtrl, idx) => {
        const colKey = hdrCtrl.value.replace(/\s*meses?$/i, '').trim();
        const cellValue = cells[idx];

        if (cellValue !== '' && cellValue !== null && cellValue !== undefined) {
          if (!matrix[colKey]) matrix[colKey] = {};
          matrix[colKey][rowKey] = +cellValue;
        }
      });
    });
    return matrix;
  }

  obtenerTax(): any {
    const tax = 21;
    return tax;
  }

  removePercentage(matrix: Matrix, pct: number): Matrix {
    // ya no usamos pct ni factor, se podría hasta eliminar este método
    return this.adjust(matrix);
  }

  adjust(source: Matrix): Matrix {
    const clone: Matrix = {};

    Object.entries(source).forEach(([months, rows]) => {
      clone[months] = {};
      Object.entries(rows).forEach(([km, value]) => {
        // Solo dividir entre 1.21 y redondear a 2 decimales
        clone[months][km] = +(value / (1 + this.taxRate)).toFixed(2);
      });
    });

    return clone;
  }
  onCheckboxChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.ivaEnabled = true;
      this.applyIva();
    } else {
      this.ivaEnabled = false;
      this.removeIva();
    }
    this.ivaToggled.emit(this.ivaEnabled);
  }

  emitDimensionsChanged(): void {
    const currentHeaders = this.headers.controls.map(h => h.value);
    const currentRowLabels = this.rows.controls.map(r => r.get('label')?.value);
    this.matrixDimensionsChanged.emit({ headers: currentHeaders, rowLabels: currentRowLabels });
  }

  getMatrixDimensions(): { headers: string[], rowLabels: string[] } {
    return {
      headers: this.headers.controls.map(h => h.value),
      rowLabels: this.rows.controls.map(r => r.get('label')?.value),
    };
  }

  trackByIndex(_index: number, _item: unknown) { return _index; }

  getValues(): any {
    let matrix: any
    let matrix_iva: any


    if (this.ivaEnabled) {
      matrix_iva = this.obtenerMatriz();
      matrix = this.removePercentage(matrix_iva, 21);
    } else {
      setTimeout(() => {
        if (this.ivaCheckbox?.nativeElement) {
          this.ivaCheckbox.nativeElement.checked = true;
        }
      });
      this.applyIva();
      matrix_iva = this.obtenerMatriz();
      matrix = this.removePercentage(matrix_iva, 21);
    }

    return {
      ...this.contractForm.getRawValue(),
      matrix_base: matrix,
      matrix_tax: this.obtenerTax(),
      matrix_total: matrix_iva,
    };
  }
}


